import {Badge} from "@/components/ui/badge";
import {ResidentRedactedLabel} from "@/src/modules/core/components/resident-redacted-label";
import {TruncatedWithTooltip} from "@/src/modules/core/components";
import {formatDate, formatTime} from "@/src/modules/core/lib";
import type {ResidentMembership} from "@/src/modules/core/types/resident-membership";
import {getTowBadgeState} from "@/src/modules/citations/lib/tow-workflow";
import type {Citation, CitationTypeEmbedded, CitationVehicle} from "@/src/modules/citations/types";
import {cn} from "@/lib/utils";

const badgeClassName = "inline-flex max-w-full truncate px-1.5 text-xs font-medium";

function formatCitationVehicleMeta(vehicle: CitationVehicle | null | undefined): string | null {
  if (!vehicle) return null;

  const normalize = (value: string | null | undefined) => {
    const trimmed = value?.trim() ?? "";
    return trimmed === "-" || trimmed === "—" ? "" : trimmed;
  };

  const parts = [normalize(vehicle.make), normalize(vehicle.model)].filter(
    (value) => value.length > 0,
  );

  return parts.length > 0 ? parts.join(" · ") : null;
}

function citationTypeDisplayLabel(t: CitationTypeEmbedded): string | null {
  const label = t.name?.trim() || t.description?.trim() || t.type?.trim();
  return label || null;
}


function CitationTowBadge({citation}: {citation: Citation}) {
  const towBadge = getTowBadgeState(citation);
  if (!towBadge.showWorkflow) return null;

  return (
    <TruncatedWithTooltip content={towBadge.label} className="block w-fit max-w-full min-w-0">
      <Badge variant={towBadge.variant} className={badgeClassName}>
        {towBadge.label}
      </Badge>
    </TruncatedWithTooltip>
  );
}

type ResidentHomeCitationsPreviewRowProps = {
  citation: Citation;
  membership?: ResidentMembership | null;
  onSelect: (id: number) => void;
  className?: string;
};

export function ResidentHomeCitationsPreviewRow({
  citation,
  membership,
  onSelect,
  className,
}: ResidentHomeCitationsPreviewRowProps) {
  const plate = citation.vehicle?.plate;
  const vehicleMeta = formatCitationVehicleMeta(citation.vehicle);
  const typeLabel = (citation.citation_types ?? [])
    .map(citationTypeDisplayLabel)
    .find((label): label is string => Boolean(label));
  const dateIso = citation.citation_date;

  return (
    <button
      type="button"
      onClick={() => onSelect(citation.citation_id)}
      className={cn(
        "hover:bg-muted/50 flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="min-w-0 truncate text-sm font-medium tabular-nums">
          <ResidentRedactedLabel value={plate} membership={membership} className="text-foreground" />
          {vehicleMeta ? (
            <span className="text-muted-foreground ml-1.5 text-xs font-normal">({vehicleMeta})</span>
          ) : null}
        </p>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {dateIso ? (
            <span className="text-muted-foreground min-w-0 truncate text-xs leading-snug tabular-nums">
              {formatDate(dateIso)}, {formatTime(dateIso)}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
          {typeLabel ? (
            <Badge variant="outline" className="shrink-0 font-normal">
              {typeLabel}
            </Badge>
          ) : null}
        </div>
      </div>

      {getTowBadgeState(citation).showWorkflow ? (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <CitationTowBadge citation={citation} />
        </div>
      ) : null}
    </button>
  );
}
